// lib/user-management/types.ts
// Staff portal ACL types, role templates, and sync path checks.

import {
  ADMIN_ROUTE_CATALOG,
  GTM_PATH,
  migrateLegacyAdminPath,
  STAFF_ACCESS_MANAGEMENT_PATH,
  STAFF_SETTINGS_PATH,
} from "./route-catalog";

export type StaffAdminRole =
  | "super_admin"
  | "developer"
  | "content_ops"
  | "custom";

export type StaffAdminStatus = "pending" | "active" | "disabled";

export type StaffAccess = {
  email: string;
  role: StaffAdminRole;
  allowedPathPrefixes: readonly string[];
  homePath: string;
  status: StaffAdminStatus;
  /** Free-form team tags (sales, marketing, delivery, hiring, ...) — used to
   * staff team tags (ops metadata only — not Calls authorization). */
  team: readonly string[];
  /** Present when loaded from DB row. */
  id?: string;
};

/** Break-glass supers — never lock yourself out of settings. */
export const BREAK_GLASS_SUPER_ADMIN_EMAILS: readonly string[] = [
  "oli@howthef.ai",
  "oli+33@fluxos.ai",
];

export function isBreakGlassSuperAdmin(email: string | undefined): boolean {
  if (!email) return false;
  return BREAK_GLASS_SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
}

export function breakGlassStaffAccess(email: string): StaffAccess {
  return {
    email: email.toLowerCase(),
    role: "super_admin",
    allowedPathPrefixes: ["*"],
    homePath: "/admin/operations",
    status: "active",
    team: [],
  };
}

/** Default prefixes + home when inviting with a role template. */
export const ROLE_TEMPLATE_DEFAULTS: Record<
  Exclude<StaffAdminRole, "custom">,
  { allowedPathPrefixes: readonly string[]; homePath: string }
> = {
  super_admin: {
    allowedPathPrefixes: ["*"],
    homePath: "/admin/operations",
  },
  content_ops: {
    // Content team members get operational-platform access (overview nav,
    // this-week, mission-control, brain/calls, tasks, client roadmaps, CRM
    // company/people/lists/activity for client context, experiments, agent
    // console, health, me, news, templates) — excluding sensitive
    // sales/finance/admin surfaces (finance, crm/deals, crm/settings, SDR,
    // outbound, network, staff-access settings, goals, code-reviews).
    // Granting a child prefix without the parent is fine — the sidebar and
    // accessCanReachPath both surface/allow the parent shell when any child
    // is reachable (see operations layout nav filter).
    allowedPathPrefixes: [
      "/admin/operations/marketing",
      "/admin/operations/seo",
      "/admin/operations/this-week",
      "/admin/operations/mission-control",
      "/admin/operations/brain",
      "/admin/operations/tasks",
      "/admin/operations/client-roadmaps",
      "/admin/operations/experiments",
      "/admin/operations/crm/companies",
      "/admin/operations/crm/people",
      "/admin/operations/crm/lists",
      "/admin/operations/crm/activity",
      "/admin/operations/agent-console",
      "/admin/operations/health",
      "/admin/operations/me",
      "/admin/news",
      "/admin/templates",
    ],
    homePath: "/admin/operations/marketing/content",
  },
  developer: {
    allowedPathPrefixes: [
      "/admin/operations/mission-control",
      "/admin/operations/code-reviews",
      "/admin/operations/tasks",
      "/admin/operations/feedback",
      "/admin/operations/marketing",
      "/admin/operations/agents",
      "/admin/operations/client-roadmaps",
      "/admin/operations/experiments",
      "/admin/operations/crm/companies",
      "/admin/operations/crm/people",
      "/admin/operations/crm/lists",
      "/admin/operations/crm/activity",
      "/admin/operations/agent-console",
      "/admin/operations/seo",
      "/admin/operations/health",
      "/admin/operations/ai-ready-data",
      "/admin/operations/autonomy",
      "/admin/operations/activity",
      "/admin/operations/inbox",
      "/admin/operations/roadmap",
      "/admin/operations/gtm",
      "/admin/news",
      "/admin/templates",
      "/admin/sdr-brain",
    ],
    homePath: "/admin/operations/agent-console",
  },
};

export function defaultsForRoleTemplate(role: StaffAdminRole): {
  allowedPathPrefixes: string[];
  homePath: string;
} {
  if (role === "custom") {
    return { allowedPathPrefixes: [], homePath: "/admin/operations/marketing" };
  }
  const d = ROLE_TEMPLATE_DEFAULTS[role];
  return {
    allowedPathPrefixes: [...d.allowedPathPrefixes],
    homePath: d.homePath,
  };
}

export function matchesPathPrefix(pathname: string, prefix: string): boolean {
  if (prefix === "*") return true;
  if (pathname === prefix) return true;
  return pathname.startsWith(`${prefix}/`);
}

const CRM_BOARD_ENTITY_TYPES = new Set(["companies", "people", "deals"]);
const BOARD_ENTITY_TYPE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const FORBIDDEN_BOARD_ENTITY_PATH =
  "/admin/operations/__forbidden_board_entity__";

export function boardEntityPagePath(entityType: string): string | null {
  if (!BOARD_ENTITY_TYPE_PATTERN.test(entityType)) {
    return null;
  }
  if (CRM_BOARD_ENTITY_TYPES.has(entityType)) {
    return `/admin/operations/crm/${entityType}`;
  }
  return `/admin/operations/${entityType}`;
}

/**
 * Map /api/admin/... BFF paths onto the page prefixes they serve so
 * production API gating stays aligned with page allowlists.
 */
export function normalizeAdminPathnameForAcl(pathname: string): string {
  if (!pathname.startsWith("/api/admin")) {
    return pathname;
  }
  const rest = pathname.slice("/api/admin".length) || "";
  if (rest.startsWith("/staff-access")) {
    // Management of OTHER people's access: super admin only.
    return STAFF_ACCESS_MANAGEMENT_PATH;
  }
  if (rest.startsWith("/api-tokens")) {
    // A person's own API tokens: any active staff member. Ownership is enforced
    // by the backend against the verified bearer, so this only has to get them
    // as far as the page they live on.
    return STAFF_SETTINGS_PATH;
  }
  if (rest.startsWith("/content")) {
    return "/admin/operations/marketing";
  }
  if (rest.startsWith("/seo")) {
    return "/admin/operations/seo";
  }
  if (rest.startsWith("/news")) {
    return "/admin/news";
  }
  if (rest === "/sdr" || rest.startsWith("/sdr/")) {
    // The SDR engine API. Its only caller is the GTM face.
    return GTM_PATH;
  }
  if (
    rest.startsWith("/templates") ||
    rest.startsWith("/template-categories")
  ) {
    return "/admin/templates";
  }
  const segments = rest.split("/").filter(Boolean);
  // Entity routes carry their access-policy prefix in the path. Saved-view and
  // custom-field-definition routes still carry entity type only in a query
  // parameter, so they remain on the CRM parent until a non-CRM UI adopts an
  // explicit page-prefix contract.
  if (segments[0] === "boards") {
    const entityRoutePrefix = "/boards/entities/";
    if (rest === "/boards/entities" || rest.startsWith(entityRoutePrefix)) {
      const entityType = rest.startsWith(entityRoutePrefix)
        ? rest.slice(entityRoutePrefix.length).split("/", 1)[0]
        : "";
      return boardEntityPagePath(entityType) ?? FORBIDDEN_BOARD_ENTITY_PATH;
    }
    return "/admin/operations/crm";
  }
  // Routes nested under /api/admin/operations/<...>/ serve the
  // /admin/operations/<...> page area (crm, brain, agent-console, ...).
  // Preserve the full sub-path so per-child prefixes (e.g.
  // /admin/operations/crm/companies) gate their matching API routes too.
  // Without this, the first segment ("operations") produced a bogus
  // /admin/operations/operations prefix that no allowlist ever grants,
  // 401-ing every non-super-admin API call under /api/admin/operations/* in
  // production.
  if (segments[0] === "operations") {
    // The page-comments layer is mounted on every operations page, so its BFF
    // has no page of its own. Normalizing it to /admin/operations/ops-comments
    // 403'd every non-super-admin in the proxy before the request ever reached
    // the backend, which already checks the commenter's grant against the
    // commented page. Any active staff member gets through here.
    if (segments[1] === "ops-comments") return OPERATIONS_HUB_PATH;
    // Task comments live on the tasks page; gate them on that grant.
    if (segments[1] === "task-comments") return "/admin/operations/tasks";
    const sub = segments.slice(1).join("/");
    return sub ? `/admin/operations/${sub}` : "/admin/operations";
  }
  const first = segments[0];
  if (first) {
    return `/admin/operations/${first}`;
  }
  return "/admin/operations";
}

/**
 * The Operations hub itself: a links/dashboard shell with no sensitive
 * content of its own — its page gate (isAdminEmailAsync) only requires
 * *any* active staff row, not a specific prefix. Every staff member should
 * be able to land there even if none of their granted prefixes happens to
 * be an exact match for it (a child prefix like /admin/operations/marketing
 * does NOT imply the bare parent — see matchesPathPrefix). Without this,
 * any non-super-admin whose allowlist is all child prefixes gets bounced
 * off the Overview page back to their home path on every visit, which
 * looks like "can't navigate anywhere in admin".
 */
const OPERATIONS_HUB_PATH = "/admin/operations";

/**
 * Release notes: what shipped, in plain English. No customer data, no
 * sensitive surface of its own, and the whole point is that the entire team
 * reads it. Granting it per-role would mean every existing staff row needs a
 * new prefix before anyone could see it, so it sits alongside the Operations
 * hub as reachable by any active staff member.
 */
const CHANGELOG_PATH = "/admin/operations/changelog";

const UNIVERSAL_STAFF_PATHS: readonly string[] = [
  OPERATIONS_HUB_PATH,
  CHANGELOG_PATH,
  // Every staff member manages their own API tokens here. The staff-access
  // half of the page is still gated, by STAFF_ACCESS_MANAGEMENT_PATH below and
  // by the tab shell, which only renders that tab for a super admin.
  STAFF_SETTINGS_PATH,
];

export function accessCanReachPath(
  access: StaffAccess | null,
  pathname: string,
): boolean {
  if (!access) return false;
  if (access.status === "disabled") return false;

  const path = normalizeAdminPathnameForAcl(pathname);

  if (
    matchesPathPrefix(path, STAFF_ACCESS_MANAGEMENT_PATH) &&
    access.role !== "super_admin"
  ) {
    return false;
  }

  if (access.role === "super_admin") return true;
  if (access.allowedPathPrefixes.includes("*")) return true;
  if (UNIVERSAL_STAFF_PATHS.includes(path)) return true;

  // Grants are migrated on read from the DB, but a StaffAccess built any other
  // way (sync env list, tests, an unmigrated row) may still carry a legacy
  // path, so resolve it here too. A legacy SDR grant keeps reaching GTM and
  // the SDR engine API.
  return access.allowedPathPrefixes.map(migrateLegacyAdminPath).some(
    (prefix) =>
      matchesPathPrefix(path, prefix) ||
      // Parent shell: a grant of /crm/companies also opens /crm (overview +
      // board APIs that normalize to the CRM root). Mirrors the sidebar, which
      // already surfaces a parent when any child is reachable. Does not open
      // siblings — /crm/deals stays closed if only /crm/companies is granted.
      matchesPathPrefix(prefix, path),
  );
}

export function accessNavVisible(
  access: StaffAccess | null,
  navPath: string,
): boolean {
  return accessCanReachPath(access, navPath);
}

export function catalogPaths(): string[] {
  return ADMIN_ROUTE_CATALOG.map((item) => item.path);
}
